import { useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { SparkActivityPoint } from '../../types/spark-activity';
import { SortablePointItem } from './SortablePointItem';

interface PointsListProps<T extends SparkActivityPoint> {
  points: T[];
  onChange: (points: T[]) => void;
  error?: string;
  max?: number;
  testId?: string;
}

export default function PointsList<T extends SparkActivityPoint>({
  points,
  onChange,
  error,
  max = 10,
  testId,
}: PointsListProps<T>) {
  const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = points.findIndex((p) => p._id === active.id);
      const newIndex = points.findIndex((p) => p._id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(points, oldIndex, newIndex).map((p, i) => ({
          ...p,
          sortOrder: i,
        }));
        onChange(reordered);
      }
    }
  };

  const handleAdd = () => {
    const newPoints = [...points, { _id: crypto.randomUUID(), text: '', sortOrder: points.length } as T];
    onChange(newPoints);
    setTimeout(() => {
      inputRefs.current[newPoints.length - 1]?.focus();
    }, 0);
  };

  const handleRemove = (index: number) => {
    setPendingRemoveIndex(null);
    onChange(points.filter((_, i) => i !== index).map((p, i) => ({ ...p, sortOrder: i })));
  };

  const handleTextChange = (index: number, text: string) => {
    onChange(points.map((p, i) => (i === index ? { ...p, text } : p)));
  };

  const canRemove = points.length > 1;
  const canAdd = points.length < max;
  const dragDisabled = pendingRemoveIndex !== null;

  const addButton = (label: string) =>
    canAdd ? (
      <button
        type="button"
        onClick={handleAdd}
        className="text-[12px] text-pine-ink-soft border-[1.5px] border-dashed border-pine-line rounded-[8px] p-[6px] w-full hover:border-pine-pine hover:text-pine-pine transition-colors"
      >
        {label}
      </button>
    ) : null;

  if (points.length === 0) {
    return (
      <div data-testid={testId}>
        {error && <p className="text-[11px] text-pine-danger mb-[6px]">{error}</p>}
        {addButton('+ Add your first point')}
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={points.map((p) => p._id!)} strategy={verticalListSortingStrategy}>
          {points.map((point, index) => (
            <SortablePointItem
              key={`point-${point._id}`}
              id={point._id!}
              index={index}
              text={point.text}
              canRemove={canRemove}
              pendingRemove={pendingRemoveIndex === index}
              disabled={dragDisabled}
              onTextChange={handleTextChange}
              onRemoveClick={setPendingRemoveIndex}
              onRemoveConfirm={handleRemove}
              onRemoveCancel={() => setPendingRemoveIndex(null)}
              textareaRef={(el) => { inputRefs.current[index] = el; }}
            />
          ))}
        </SortableContext>
      </DndContext>
      {error && <p className="text-[11px] text-pine-danger mb-[6px]">{error}</p>}
      {addButton('+ Add point')}
    </div>
  );
}
